import { useEffect, useState } from "react";
import { useAuth } from "../../../context/UserContextProvider";
import SingleProfileItem from "../../../components/learner/user-profiles/SingleProfileItem";
import i18n from "../../../language/i18n";
import { useApp } from "../../../context/AppContextProvider";
import {
  isLearnerProfileDeactivatedError,
  rememberDeactivatedProfile,
} from "../../../utilization/DeactivatedProfileUtilization";
import { NotificationManager } from "../../../notifications/NotificationManager";
import {
  patchUserOnlyRequiredAction,
  retrieveUserProfileTokenInformation,
  retrieveUserProfileList,
} from "../../../requests/ManagementRequests";
import RightsReserved from "../../../components/common/landing/RightsReserved";
import { BaseLoadingWithText, SecondaryButton } from "../../../library";
import { useNavigate } from "react-router-dom";
import { profileSelectionScreenStyles } from "./ProfileSelectionScreenStyles";
import {
  isImpersonatingBySession,
  updateImpersonationTarget,
} from "../../../utilization/ImpersonationSessionUtilization";

const ACCENTS = ["teal", "coral", "purple"] as const;

const ProfileSelectionScreen = () => {
  const navigate = useNavigate();
  const { setBoarding, isFullScreen, setIsFullScreen } = useApp();
  const {
    handleStateLogin,
    handleStateLogout,
    handleStateChooseProfile,
    userInformation,
    userProfileDTOList,
    setUserProfileDTOList,
  } = useAuth();
  const [loadingProfileId, setLoadingProfileId] = useState(null);
  const [profileListLoading, setProfileListLoading] = useState(false);

  useEffect(() => {
    if (isFullScreen === false) {
      setIsFullScreen(true);
    }
  }, []);

  useEffect(() => {
    const fetchProfileList = async () => {
      setProfileListLoading(true);
      const { data, err } = await retrieveUserProfileList();
      if (!err && data) {
        setUserProfileDTOList(data);
      }
      setProfileListLoading(false);
    };
    fetchProfileList();
  }, []);

  const filterLearnerUserProfileDTOList = () => {
    return userProfileDTOList.filter(
      (userProfileDTO) => userProfileDTO.role === "LEARNER"
    );
  };

  const filterActiveLearnerUserProfileDTOList = (learnerUserProfileDTOList) => {
    return learnerUserProfileDTOList.filter((learneruserProfileDTO) => {
      const { branchDTO } = learneruserProfileDTO;
      const { gradeDTO } = branchDTO;
      const { seasonDTO } = gradeDTO;
      return (
        seasonDTO.beginAt < new Date().getTime() &&
        seasonDTO.endAt > new Date().getTime()
      );
    });
  };

  const handleOnClick = async (selectedUserProfile) => {
    setLoadingProfileId(selectedUserProfile.id);
    // An impersonated session has NO refresh token — that absence is what bounds it to 30 minutes — so it
    // exchanges its current (`act`-bearing) access token instead: the global axios interceptor sends it, and
    // `/authenticate/with-profile` routes it to the impersonation path (same `act`, same session id, same
    // expiry, still no refresh token, and the profile re-checked against the impersonating admin's brands).
    // That covers the first pick after the redeem and every "Profil değiştir" inside the session.
    //
    // The LIVE TOKEN's `act` claim decides, not the storage flag. The flag has a 12-hour horizon and is
    // cleared by only three paths — none of them the ordinary `/login` route — so an abandoned impersonation
    // left it set for the NEXT person to sign in on this browser; asking the token cannot go stale.
    const impersonating = isImpersonatingBySession();
    const refreshToken = localStorage.getItem("refreshToken");
    if (!refreshToken && !impersonating) {
      setLoadingProfileId(null);
      return;
    }
    const { data, err } = await retrieveUserProfileTokenInformation(
      selectedUserProfile.id,
      impersonating ? null : refreshToken
    );
    if (err) {
      setLoadingProfileId(null);
      // Pasif öğrenci: the school has not opened the platform for this profile — a full page, not a toast.
      if (isLearnerProfileDeactivatedError(err)) {
        rememberDeactivatedProfile(selectedUserProfile);
        setBoarding("PROFILE_INACTIVE");
        return;
      }
      if (impersonating) {
        // The server names WHY a pick was refused (a profile outside the admin's brands, an ended season,
        // a session the admin already closed). Say so rather than leaving a dead click.
        const refusalKey = (err as any)?.response?.data;
        NotificationManager.error(
          i18n.t(
            typeof refusalKey === "string" && i18n.exists(refusalKey)
              ? refusalKey
              : "view_as_user_not_permitted"
          )
        );
      }
      return;
    }
    const { accessToken } = data as any;
    // `handleStateLogin` skips a null refresh token, so an impersonated session stays refresh-less.
    handleStateLogin(accessToken, impersonating ? null : refreshToken);
    if (impersonating) {
      // The banner names the role it is acting as; keep it in step with the pick.
      updateImpersonationTarget({
        userProfileId: selectedUserProfile.id,
        role: selectedUserProfile.role,
      });
    }
    if (selectedUserProfile.role === "LEARNER") {
      const { branchDTO } = selectedUserProfile;
      handleStateChooseProfile(
        selectedUserProfile,
        branchDTO?.gradeDTO.seasonDTO,
        branchDTO?.gradeDTO,
        branchDTO
      );
    }

    localStorage.setItem("appState", "CHANGE_PROFILE");
    if (
      userInformation.requiredAction === "PROFILE_SELECTION" ||
      userInformation.requiredAction === "LOGOUT"
    ) {
      handlePatchUserRequiredActionOnClick();
    }
    setLoadingProfileId(null);
    setBoarding("DASHBOARD");
  };

  const handlePatchUserRequiredActionOnClick = async () => {
    await patchUserOnlyRequiredAction({
      id: userInformation.id,
      requiredAction: null,
    });
  };

  const handleLogoutOnClick = () => {
    handleStateLogout();
    setBoarding("LANDING");
    navigate("/login");
    NotificationManager.success(i18n.t("logout_successful_text"));
  };

  if (profileListLoading || !userProfileDTOList) {
    return (
      <div className="rak-loading">
        <style>{profileSelectionScreenStyles}</style>
        <BaseLoadingWithText
          size="large"
          primaryColor="#57C2D3"
          secondaryColor="#57C2D3"
        />
      </div>
    );
  }

  const filteredLearnerUserProfileDTOList = filterLearnerUserProfileDTOList();
  const filteredActiveLearnerUserProfileDTOList =
    filterActiveLearnerUserProfileDTOList(filteredLearnerUserProfileDTOList);
  // During an impersonation the picker lists the user's LEARNER profiles — the same list the user would see
  // (this screen never offers anything else). It is no longer pinned to one profile: the admin chose the
  // APP on admin-web, and choosing (or later switching) the profile here is the point. The control is
  // server-side and does not depend on this list: `/authenticate/with-profile` re-checks ownership, the role
  // allow-list, the season/coterie gates and the admin's own brands for every `act`-bearing exchange.
  const combinedProfileDTOList = [...filteredActiveLearnerUserProfileDTOList];

  return (
    <div className="rak-root">
      <style>{profileSelectionScreenStyles}</style>

      <div className="rak-blobs" aria-hidden="true">
        <span className="rak-blob rak-blob--teal"></span>
        <span className="rak-blob rak-blob--purple"></span>
        <span className="rak-blob rak-blob--coral"></span>
        <span className="rak-blob rak-blob--gold"></span>
      </div>

      <div className="rak-inner">
        <img
          className="rak-logo"
          src="/logos/logo-primary-2.webp"
          alt="CatchUpper"
        />

        {userInformation?.firstName ? (
          <div className="rak-hello">
            <span className="rak-hello-text">
              {i18n.t("signed_in_as_text")} <b>{userInformation.firstName}</b>
            </span>
          </div>
        ) : null}

        <h1 className="rak-title">{i18n.t("select_profile_text")}</h1>
        <p className="rak-sub">{i18n.t("select_profile_subtext")}</p>

        {combinedProfileDTOList.length > 0 ? (
          <div className="rak-grid">
            {combinedProfileDTOList.map((userProfileDTO, index) => (
              <SingleProfileItem
                key={userProfileDTO.id}
                {...({
                  userProfile: userProfileDTO,
                  selectedProfileId: loadingProfileId,
                  type: i18n.t(userProfileDTO.role),
                  accent: ACCENTS[index % ACCENTS.length],
                  onClick: () => {
                    handleOnClick(userProfileDTO);
                  },
                } as any)}
              />
            ))}
          </div>
        ) : (
          <div className="rak-empty">
            <p className="rak-empty-text">
              {i18n.t("you_do_not_have_any_user_profiles_yet")}
            </p>
          </div>
        )}

        <div className="rak-switch-slot">
          <SecondaryButton
            {...({
              title: i18n.t("sign_in_with_different_account_text"),
              size: "medium",
              textOnly: true,
              onClick: handleLogoutOnClick,
            } as any)}
          />
        </div>
      </div>

      <div className="rak-rights">
        <RightsReserved />
      </div>
    </div>
  );
};

export default ProfileSelectionScreen;
